import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

interface StakingDeposit {
  id: string;
  amount: number;
  packageMonths: number;
  monthlyRate: number;
  totalEarned: number;
  accruedIncome: number;
  dailyIncome: number;
  status: string;
  startedAt: string;
  endsAt: string;
  lastClaimAt: string | null;
  daysRemaining: number;
  isIncomeActive: boolean;
}

interface StakingData {
  stakingBalance: number;
  totalBlocked: number;
  totalEarned: number;
  totalAccrued: number;
  canClaim: boolean;
  nextClaimAt: string | null;
  cooldownSeconds: number;
  deposits: StakingDeposit[];
  balance: number;
}

interface StakingConfig {
  packages: { months: number; monthlyRate: number; minAmount: number; maxAmount: number }[];
  depositFeePercent: number;
  toMainFeePercent: number;
  burningFee: number;
  withdrawBurningFee: number;
  witonToUsdtRate: number;
}

interface UsdtStakingData {
  stakingBalance: number;
  totalBlocked: number;
  totalEarned: number;
  totalAccrued: number;
  canClaim: boolean;
  nextClaimAt: string | null;
  cooldownSeconds: number;
  deposits: StakingDeposit[];
  usdtBalance: number;
  witonBalance: number;
}

interface UsdtStakingConfig {
  packages: { months: number; monthlyRate: number; minAmount: number; maxAmount: number }[];
  depositFeePercent: number;
  toMainFeePercent: number;
  burningFeeWiton: number;
  burningFeeUsdt: number;
  withdrawBurningFeeWiton: number;
  withdrawBurningFeeUsdt: number;
}

const PACKAGE_COLORS: Record<number, string> = {
  3: '#10b981',
  6: '#3b82f6',
  12: '#8b5cf6',
  36: '#f59e0b',
  60: '#ef4444',
};

const styles = {
  page: { minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)', color: '#fff', paddingBottom: '100px' } as React.CSSProperties,
  header: { padding: '20px 16px 12px', textAlign: 'center' as const },
  card: { background: 'rgba(0,0,0,0.3)', borderRadius: '14px', padding: '16px', marginBottom: '12px', border: '1px solid rgba(255,255,255,0.06)' } as React.CSSProperties,
  btn: (active: boolean, color: string) => ({
    width: '100%', padding: '14px', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: 700,
    cursor: active ? 'pointer' : 'not-allowed',
    background: active ? `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` : 'rgba(255,255,255,0.1)',
    opacity: active ? 1 : 0.5,
  } as React.CSSProperties),
  input: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px', color: '#fff', fontSize: '16px', outline: 'none' } as React.CSSProperties,
};

type View = 'main' | 'deposit' | 'withdraw' | 'transfer';
type StakingTab = 'witon' | 'usdt';

export function StakingScreen() {
  const [tab, setTab] = useState<StakingTab>('witon');
  // WiTon state
  const [data, setData] = useState<StakingData | null>(null);
  const [config, setConfig] = useState<StakingConfig | null>(null);
  // USDT state
  const [usdtData, setUsdtData] = useState<UsdtStakingData | null>(null);
  const [usdtConfig, setUsdtConfig] = useState<UsdtStakingConfig | null>(null);

  const [view, setView] = useState<View>('main');
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [selectedPkg, setSelectedPkg] = useState<number | null>(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositing, setDepositing] = useState(false);

  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawWallet, setWithdrawWallet] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const [transferAmount, setTransferAmount] = useState('');
  const [transferring, setTransferring] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, configRes, usdtStatusRes, usdtConfigRes] = await Promise.all([
        api.getStakingStatus(),
        api.getStakingConfig(),
        api.getUsdtStakingStatus(),
        api.getUsdtStakingConfig(),
      ]);
      setData(statusRes.data.data);
      setConfig(configRes.data.data);
      setUsdtData(usdtStatusRes.data.data);
      setUsdtConfig(usdtConfigRes.data.data);
      setCooldown(tab === 'witon' ? statusRes.data.data.cooldownSeconds || 0 : usdtStatusRes.data.data.cooldownSeconds || 0);
    } catch (e) {
      console.error('Staking fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const activeData = tab === 'witon' ? data : usdtData;
  const activeConfig = tab === 'witon' ? config : usdtConfig;
  const isUsdt = tab === 'usdt';
  const currency = isUsdt ? 'USDT' : 'WiTon';

  useEffect(() => {
    if (!activeData || activeData.deposits.length === 0) return;
    const t = setInterval(() => {
      const setter = isUsdt ? setUsdtData : setData;
      setter((prev: any) => {
        if (!prev) return prev;
        let totalAccrued = 0;
        const deposits = prev.deposits.map((d: StakingDeposit) => {
          if (!d.isIncomeActive) { totalAccrued += d.accruedIncome; return d; }
          const perSecond = d.amount * d.monthlyRate / 100 / (30 * 24 * 3600);
          const newAccrued = d.accruedIncome + perSecond;
          totalAccrued += newAccrued;
          return { ...d, accruedIncome: newAccrued };
        });
        return { ...prev, deposits, totalAccrued };
      });
    }, 1000);
    return () => clearInterval(t);
  }, [activeData?.deposits.length, isUsdt]);

  useEffect(() => {
    setView('main');
    setSelectedPkg(null);
    setDepositAmount('');
    setWithdrawAmount('');
    setWithdrawWallet('');
    setTransferAmount('');
    if (tab === 'witon' && data) setCooldown(data.cooldownSeconds || 0);
    if (tab === 'usdt' && usdtData) setCooldown(usdtData.cooldownSeconds || 0);
  }, [tab]);

  const handleClaim = async () => {
    if (!activeData?.canClaim || claiming) return;
    setClaiming(true);
    try {
      if (isUsdt) await api.usdtStakingClaim();
      else await api.stakingClaim();
      await fetchData();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Claim failed');
    } finally {
      setClaiming(false);
    }
  };

  const handleDeposit = async () => {
    if (!selectedPkg || !depositAmount || depositing) return;
    setDepositing(true);
    try {
      if (isUsdt) await api.usdtStakingDeposit(Number(depositAmount), selectedPkg);
      else await api.stakingDeposit(Number(depositAmount), selectedPkg);
      setView('main');
      setDepositAmount('');
      setSelectedPkg(null);
      await fetchData();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Deposit failed');
    } finally {
      setDepositing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || !withdrawWallet || withdrawing) return;
    setWithdrawing(true);
    try {
      if (isUsdt) await api.usdtStakingWithdraw(Number(withdrawAmount), withdrawWallet.trim());
      else await api.stakingWithdraw(Number(withdrawAmount), withdrawWallet.trim());
      alert('Withdrawal request submitted!');
      setView('main');
      setWithdrawAmount('');
      setWithdrawWallet('');
      await fetchData();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferAmount || transferring) return;
    setTransferring(true);
    try {
      if (isUsdt) await api.usdtStakingTransferToMain(Number(transferAmount));
      else await api.stakingTransferToMain(Number(transferAmount));
      setView('main');
      setTransferAmount('');
      await fetchData();
    } catch (e: any) {
      alert(e.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const fmtAmount = (n: number) => isUsdt ? `$${n.toFixed(2)}` : Math.floor(n).toLocaleString();
  const fmtBalance = (n: number) => isUsdt ? `$${n.toFixed(2)}` : Math.floor(n).toLocaleString();

  if (loading) return <div style={styles.page}><div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading...</div></div>;
  if (!activeData || !activeConfig) return <div style={styles.page}><div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Failed to load staking</div></div>;

  const tabBar = (
    <div style={{ display: 'flex', gap: '4px', margin: '0 16px 12px', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '4px' }}>
      <button onClick={() => setTab('witon')} style={{
        flex: 1, padding: '10px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
        background: tab === 'witon' ? 'rgba(16,185,129,0.2)' : 'transparent',
        color: tab === 'witon' ? '#10b981' : 'rgba(255,255,255,0.5)',
      }}>
        WiTon Staking
      </button>
      <button onClick={() => setTab('usdt')} style={{
        flex: 1, padding: '10px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
        background: tab === 'usdt' ? 'rgba(45,125,50,0.2)' : 'transparent',
        color: tab === 'usdt' ? '#2e7d32' : 'rgba(255,255,255,0.5)',
      }}>
        💵 USDT Staking
      </button>
    </div>
  );

  if (view === 'deposit') return renderDepositView();
  if (view === 'withdraw') return renderWithdrawView();
  if (view === 'transfer') return renderTransferView();
  return renderMainView();

  function renderMainView() {
    const d = activeData!;
    const accentColor = isUsdt ? '#2e7d32' : '#10b981';
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 4px' }}>🏦 Staking</h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
            {isUsdt ? 'Earn passive income on your USDT' : 'Earn passive income on your WiTon'}
          </p>
        </div>

        {tabBar}

        <div style={{ padding: '0 16px' }}>
          <div style={{ ...styles.card, border: `1px solid ${accentColor}33`, background: `${accentColor}0d` }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>{currency} Staking Balance</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: accentColor }}>
              {fmtBalance(d.stakingBalance)} <span style={{ fontSize: '14px', fontWeight: 400 }}>{currency}</span>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px', fontSize: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'rgba(255,255,255,0.4)' }}>Blocked</div>
                <div style={{ fontWeight: 700, color: '#f59e0b' }}>{fmtAmount(d.totalBlocked)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'rgba(255,255,255,0.4)' }}>Total Earned</div>
                <div style={{ fontWeight: 700, color: accentColor }}>{fmtAmount(d.totalEarned)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'rgba(255,255,255,0.4)' }}>Accrued</div>
                <div style={{ fontWeight: 700, color: '#3b82f6' }}>{fmtAmount(d.totalAccrued)}</div>
              </div>
            </div>
          </div>

          {d.deposits.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <button onClick={handleClaim} disabled={!d.canClaim || claiming} style={styles.btn(d.canClaim && !claiming, accentColor)}>
                {claiming ? 'Claiming...' : d.canClaim
                  ? `Claim ${fmtAmount(d.totalAccrued)} ${currency}`
                  : `Next claim in ${formatTime(cooldown)}`
                }
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button onClick={() => setView('deposit')} style={{ ...styles.btn(true, '#3b82f6'), flex: 1, padding: '12px', fontSize: '13px' }}>
              Deposit
            </button>
            <button onClick={() => setView('withdraw')} disabled={d.stakingBalance <= 0} style={{ ...styles.btn(d.stakingBalance > 0, '#8b5cf6'), flex: 1, padding: '12px', fontSize: '13px' }}>
              Withdraw
            </button>
            <button onClick={() => setView('transfer')} disabled={d.stakingBalance <= 0} style={{ ...styles.btn(d.stakingBalance > 0, '#f59e0b'), flex: 1, padding: '12px', fontSize: '13px' }}>
              → Main
            </button>
          </div>

          {d.deposits.length > 0 && (
            <>
              <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '10px' }}>Active Deposits</div>
              {d.deposits.map(dep => {
                const color = PACKAGE_COLORS[dep.packageMonths] || '#888';
                return (
                  <div key={dep.id} style={{ ...styles.card, borderColor: `${color}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div>
                        <span style={{ fontSize: '16px', fontWeight: 800, color }}>{dep.packageMonths}M</span>
                        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginLeft: '8px' }}>{dep.monthlyRate}%/mo</span>
                      </div>
                      <span style={{ fontSize: '11px', color, padding: '2px 8px', borderRadius: '10px', background: `${color}18` }}>
                        {dep.daysRemaining}d left
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>Locked</span>
                      <span style={{ fontWeight: 700 }}>{fmtAmount(dep.amount)} {currency}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>Daily Income</span>
                      <span style={{ fontWeight: 700, color: accentColor }}>+{fmtAmount(dep.dailyIncome)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>Accrued</span>
                      <span style={{ fontWeight: 700, color: '#3b82f6' }}>{fmtAmount(dep.accruedIncome)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>Total Earned</span>
                      <span style={{ fontWeight: 700, color: accentColor }}>{fmtAmount(dep.totalEarned)}</span>
                    </div>
                    {!dep.isIncomeActive && (
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '6px 10px', borderRadius: '8px', textAlign: 'center' }}>
                        Income paused — claim to resume
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {d.deposits.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>{isUsdt ? '💵' : '🏦'}</div>
              <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>Start {currency} Staking</div>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px' }}>
                Lock your {currency} and earn up to {isUsdt ? '7.5' : '5.55'}% monthly income
              </p>
              <button onClick={() => setView('deposit')} style={styles.btn(true, '#3b82f6')}>
                Make a Deposit
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderDepositView() {
    const cfg = activeConfig!;
    const pkg = cfg.packages.find(p => p.months === selectedPkg);
    const amt = Number(depositAmount) || 0;
    const feePercent = cfg.depositFeePercent;

    let fee: number, burn: string, total: number, availableBalance: number;
    if (isUsdt) {
      const uc = cfg as UsdtStakingConfig;
      fee = Math.floor(amt * feePercent) / 100;
      burn = `${uc.burningFeeWiton.toLocaleString()} WiTon`;
      total = amt + fee;
      availableBalance = (usdtData as UsdtStakingData).usdtBalance;
    } else {
      const wc = cfg as StakingConfig;
      fee = Math.floor(amt * feePercent / 100);
      burn = `${wc.burningFee.toLocaleString()} WiTon`;
      total = amt + fee + wc.burningFee;
      availableBalance = (data as StakingData).balance;
    }
    const canDeposit = pkg && amt >= pkg.minAmount && (pkg.maxAmount === 0 || amt <= pkg.maxAmount) && availableBalance >= total;

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button onClick={() => setView('main')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', position: 'absolute' as const, left: '16px', top: '20px' }}>← Back</button>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>{currency} Staking Deposit</h1>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Balance: {isUsdt ? `$${availableBalance.toFixed(2)}` : `${Math.floor(availableBalance).toLocaleString()} WiTon`}
          </div>
        </div>

        <div style={{ padding: '0 16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '10px' }}>Select Package</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {cfg.packages.map(p => {
              const color = PACKAGE_COLORS[p.months] || '#888';
              const selected = selectedPkg === p.months;
              return (
                <button key={p.months} onClick={() => setSelectedPkg(p.months)} style={{
                  background: selected ? `${color}22` : 'rgba(0,0,0,0.3)',
                  border: `1px solid ${selected ? color : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '12px', padding: '14px', textAlign: 'left' as const, cursor: 'pointer', color: '#fff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontSize: '16px', fontWeight: 800, color }}>{p.months} months</span>
                      <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginLeft: '8px' }}>{p.monthlyRate}%/mo</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                      {isUsdt
                        ? `min $${p.minAmount}`
                        : `${(p.minAmount / 1000).toFixed(0)}K${p.maxAmount > 0 ? ` — ${p.maxAmount >= 1_000_000 ? `${(p.maxAmount / 1_000_000).toFixed(0)}M` : `${(p.maxAmount / 1000).toFixed(0)}K`}` : '+'}`
                      }
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedPkg && (
            <>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Amount ({currency})</div>
              <input
                type="number"
                value={depositAmount}
                onChange={e => setDepositAmount(e.target.value)}
                placeholder={isUsdt ? `Min $${pkg?.minAmount}` : `Min ${pkg?.minAmount?.toLocaleString()}`}
                style={{ ...styles.input, marginBottom: '12px' }}
              />

              {amt > 0 && (
                <div style={styles.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Blocked (earning interest)</span>
                    <span style={{ fontWeight: 700, color: '#f59e0b' }}>{fmtAmount(amt)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Funding Fee ({feePercent}%)</span>
                    <span style={{ color: '#ef4444' }}>-{fmtAmount(fee)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Burning Fee</span>
                    <span style={{ color: '#ef4444' }}>-{burn}</span>
                  </div>
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span>Total deducted</span>
                    <span style={{ fontWeight: 700, color: '#ef4444' }}>{fmtAmount(total)} {isUsdt ? 'USDT' : 'WiTon'}{isUsdt ? ` + ${burn}` : ''}</span>
                  </div>
                  {pkg && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                      Daily income: ~{fmtAmount(amt * pkg.monthlyRate / 100 / 30)} {currency}
                    </div>
                  )}
                </div>
              )}

              <button onClick={handleDeposit} disabled={!canDeposit || depositing} style={styles.btn(!!canDeposit && !depositing, '#3b82f6')}>
                {depositing ? 'Processing...' : `Deposit ${amt > 0 ? fmtAmount(amt) : ''} ${currency}`}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  function renderWithdrawView() {
    const cfg = activeConfig!;
    const d = activeData!;
    const amt = Number(withdrawAmount) || 0;

    if (isUsdt) {
      const uc = cfg as UsdtStakingConfig;
      const canWithdraw = amt > 0 && d.stakingBalance >= amt + uc.withdrawBurningFeeUsdt && withdrawWallet.trim().length > 0;
      return (
        <div style={styles.page}>
          <div style={styles.header}>
            <button onClick={() => setView('main')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', position: 'absolute' as const, left: '16px', top: '20px' }}>← Back</button>
            <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Withdraw USDT Staking</h1>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Available: ${d.stakingBalance.toFixed(2)} USDT</div>
          </div>
          <div style={{ padding: '0 16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Amount (USDT)</div>
            <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="0" style={{ ...styles.input, marginBottom: '12px' }} />
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>TON Wallet Address</div>
            <input type="text" value={withdrawWallet} onChange={e => setWithdrawWallet(e.target.value)} placeholder="UQD..." style={{ ...styles.input, marginBottom: '12px' }} />
            {amt > 0 && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Amount</span>
                  <span>${amt.toFixed(2)} USDT</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Burning Fee</span>
                  <span style={{ color: '#ef4444' }}>{uc.withdrawBurningFeeWiton.toLocaleString()} WiTon or ${uc.withdrawBurningFeeUsdt} USDT</span>
                </div>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span>You will receive</span>
                  <span style={{ fontWeight: 700, color: '#10b981' }}>${amt.toFixed(2)} USDT</span>
                </div>
              </div>
            )}
            <button onClick={handleWithdraw} disabled={!canWithdraw || withdrawing} style={styles.btn(!!canWithdraw && !withdrawing, '#8b5cf6')}>
              {withdrawing ? 'Processing...' : 'Request Withdrawal'}
            </button>
            <div style={{ marginTop: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
              No Funding Fee. Only Burning Fee applies.
            </div>
          </div>
        </div>
      );
    }

    // WiTon withdraw
    const wc = cfg as StakingConfig;
    const burn = wc.withdrawBurningFee;
    const rate = wc.witonToUsdtRate;
    const usdt = Math.floor(amt * rate * 100) / 100;
    const canWithdraw = amt > 0 && d.stakingBalance >= amt + burn && withdrawWallet.trim().length > 0;

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button onClick={() => setView('main')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', position: 'absolute' as const, left: '16px', top: '20px' }}>← Back</button>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Withdraw from Staking</h1>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Available: {Math.floor(d.stakingBalance).toLocaleString()} WiTon</div>
        </div>
        <div style={{ padding: '0 16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Amount (WiTon)</div>
          <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="0" style={{ ...styles.input, marginBottom: '12px' }} />
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>TON Wallet Address</div>
          <input type="text" value={withdrawWallet} onChange={e => setWithdrawWallet(e.target.value)} placeholder="UQD..." style={{ ...styles.input, marginBottom: '12px' }} />
          {amt > 0 && (
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>Amount</span>
                <span>{amt.toLocaleString()} WiTon</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>Burning Fee</span>
                <span style={{ color: '#ef4444' }}>-{burn.toLocaleString()} WiTon</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>USDT Equivalent</span>
                <span style={{ fontWeight: 700, color: '#10b981' }}>{usdt.toFixed(2)} USDT</span>
              </div>
            </div>
          )}
          <button onClick={handleWithdraw} disabled={!canWithdraw || withdrawing} style={styles.btn(!!canWithdraw && !withdrawing, '#8b5cf6')}>
            {withdrawing ? 'Processing...' : 'Request Withdrawal'}
          </button>
          <div style={{ marginTop: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
            No Funding Fee. Only {burn.toLocaleString()} WiTon Burning Fee.
          </div>
          <div style={{ marginTop: '16px', opacity: 0.4 }}>
            <div style={{ ...styles.card, border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>WiTon Withdrawal</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Withdraw WiTon to exchange or TON Wallet — coming soon</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTransferView() {
    const cfg = activeConfig!;
    const d = activeData!;
    const amt = Number(transferAmount) || 0;

    if (isUsdt) {
      const uc = cfg as UsdtStakingConfig;
      const feeUsdt = Math.floor(amt * uc.toMainFeePercent) / 100;
      const net = Math.floor((amt - feeUsdt) * 100) / 100;
      const canTransfer = amt > 0 && d.stakingBalance >= amt + uc.burningFeeUsdt;

      return (
        <div style={styles.page}>
          <div style={styles.header}>
            <button onClick={() => setView('main')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', position: 'absolute' as const, left: '16px', top: '20px' }}>← Back</button>
            <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Transfer to Main</h1>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>USDT Staking Balance: ${d.stakingBalance.toFixed(2)}</div>
          </div>
          <div style={{ padding: '0 16px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Amount (USDT)</div>
            <input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} placeholder="0" style={{ ...styles.input, marginBottom: '12px' }} />
            {amt > 0 && (
              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Funding Fee ({uc.toMainFeePercent}%)</span>
                  <span style={{ color: '#ef4444' }}>-${feeUsdt.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>Burning Fee</span>
                  <span style={{ color: '#ef4444' }}>{uc.burningFeeWiton.toLocaleString()} WiTon or ${uc.burningFeeUsdt} USDT</span>
                </div>
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span>You will receive</span>
                  <span style={{ fontWeight: 700, color: '#10b981' }}>${net.toFixed(2)} USDT</span>
                </div>
              </div>
            )}
            <button onClick={handleTransfer} disabled={!canTransfer || transferring} style={styles.btn(!!canTransfer && !transferring, '#f59e0b')}>
              {transferring ? 'Processing...' : 'Transfer to Main Balance'}
            </button>
          </div>
        </div>
      );
    }

    // WiTon transfer
    const wc = cfg as StakingConfig;
    const fee = Math.floor(amt * wc.toMainFeePercent / 100);
    const burn = wc.burningFee;
    const net = amt - fee;
    const total = amt + burn;
    const canTransfer = amt > 0 && d.stakingBalance >= total;

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <button onClick={() => setView('main')} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '16px', cursor: 'pointer', position: 'absolute' as const, left: '16px', top: '20px' }}>← Back</button>
          <h1 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>Transfer to Main</h1>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Staking Balance: {Math.floor(d.stakingBalance).toLocaleString()} WiTon</div>
        </div>
        <div style={{ padding: '0 16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Amount (WiTon)</div>
          <input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} placeholder="0" style={{ ...styles.input, marginBottom: '12px' }} />
          {amt > 0 && (
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>Funding Fee ({wc.toMainFeePercent}%)</span>
                <span style={{ color: '#ef4444' }}>-{fee.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>Burning Fee</span>
                <span style={{ color: '#ef4444' }}>-{burn.toLocaleString()}</span>
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>You will receive</span>
                <span style={{ fontWeight: 700, color: '#10b981' }}>{net.toLocaleString()} WiTon</span>
              </div>
            </div>
          )}
          <button onClick={handleTransfer} disabled={!canTransfer || transferring} style={styles.btn(!!canTransfer && !transferring, '#f59e0b')}>
            {transferring ? 'Processing...' : 'Transfer to Main Balance'}
          </button>
        </div>
      </div>
    );
  }
}
